import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AlertCircle, Clock, Calendar, Headphones, ArrowLeft, Bell, Mail, CheckCircle } from 'lucide-react'
import { useState } from 'react'
import useStore from '../store/useStore'

function ComingSoonPodcast() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useStore()

  const podcast = location.state?.podcast || {}
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [subscribed, setSubscribed] = useState(false)
  const [error, setError] = useState('')
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Coming Soon'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const handleNotifyMe = async (e) => {
    e.preventDefault()
    setError('')
    
    // Get email from form or from logged in user
    const subscribeEmail = email || user?.email
    
    if (!subscribeEmail) {
      setError('Please enter your email address')
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(subscribeEmail)) {
      setError('Please enter a valid email address')
      return
    }

    setLoading(true)
    
    try {
      // Use the dedicated podcast notification endpoint
      const response = await fetch('/api/v2/admin/podcast-notify.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: subscribeEmail,
          name: user?.name || '',
          user_id: user?.id || null,
          podcast_id: podcast.id || id,
          podcast_title: podcast.title || 'Coming Soon Podcast',
          topic: podcast.topic || 'General',
          duration: podcast.duration || 'Coming Soon',
          summary: podcast.summary || 'This episode is coming soon!',
          image: podcast.image || ''
        })
      })

      const data = await response.json()
      
      if (data.success) {
        setSubscribed(true)
        setEmail('')
        setError('')
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSubscribed(false)
        }, 5000)
      } else {
        throw new Error(data.error || 'Failed to subscribe')
      }
      
    } catch (error) {
      console.error('Subscription error:', error)
      setError('Failed to subscribe. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-8 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Podcasts</span>
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-8 text-white">
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-white/20 rounded-full">
                <AlertCircle className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Coming Soon</h1>
                <p className="text-amber-100">This episode is being prepared</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Alert */}
            <div className="mb-8 p-6 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start space-x-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-amber-800 mb-2">Audio in Progress</h3>
                  <p className="text-amber-700">
                    We're currently working on the audio recording for this episode. 
                    Check back soon or subscribe to be notified when it's available.
                  </p>
                </div>
              </div>
            </div>

            {/* Podcast Info */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              {/* Image */}
              <div>
                <div className="rounded-xl overflow-hidden shadow-lg mb-4">
                  <img 
                    src={podcast.image || 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=400&h=300&fit=crop'} 
                    alt={podcast.title || 'Coming Soon Podcast'}
                    className="w-full h-48 object-cover"
                  />
                </div>
                <button
                  onClick={() => navigate('/podcasts')}
                  className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Browse Other Podcasts
                </button>
              </div>

              {/* Details */}
              <div className="lg:col-span-2">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{podcast.title || 'Coming Soon Episode'}</h2>
                
                {/* Badges */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {podcast.episodeType || 'Deep Dive'}
                  </span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    {podcast.tier || 'Free'}
                  </span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    {podcast.topic || 'General'}
                  </span>
                </div>

                {/* Description */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Episode Summary</h3>
                  <p className="text-gray-700 leading-relaxed">{podcast.summary || 'Episode details are coming soon. Stay tuned for an exciting new episode!'}</p>
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Duration</p>
                        <p className="font-medium">{podcast.duration || 'TBD'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Expected Release</p>
                        <p className="font-medium">{formatDate(podcast.publishDate)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Headphones className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Interest Level</p>
                        <p className="font-medium">High</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="border-t pt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">What to Expect</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-3 mb-2">
                        <Headphones className="w-5 h-5 text-blue-600" />
                        <h4 className="font-medium text-blue-900">High-Quality Audio</h4>
                      </div>
                      <p className="text-sm text-blue-700">Professional recording and sound engineering</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-3 mb-2">
                        <Bell className="w-5 h-5 text-blue-600" />
                        <h4 className="font-medium text-blue-900">Get Notified</h4>
                      </div>
                      <p className="text-sm text-blue-700">We'll email you the moment it's available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notify Me Section */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-100">
              <div className="flex items-center space-x-3 mb-4">
                <Mail className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">Get Notified When It's Ready</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                {user 
                  ? `We'll notify ${user.email} when this episode is available.` 
                  : "Enter your email below and we'll notify you the moment this episode launches."}
              </p>
              
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center space-x-2"
                >
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}
              
              {subscribed ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-50 border border-green-200 rounded-xl p-6 text-center"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-green-800 mb-2">You're all set! 🎉</h4>
                  <p className="text-green-700">
                    We'll notify you when this episode is available.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleNotifyMe} className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={user?.email || "Enter your email address"}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required={!user?.email}
                    />
                    <button 
                      type="submit"
                      disabled={loading}
                      className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-md hover:shadow-lg min-w-[140px]"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Subscribing...</span>
                        </>
                      ) : (
                        <>
                          <Bell className="w-5 h-5" />
                          <span>Notify Me</span>
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    We'll only email you about this episode. No spam, unsubscribe anytime.
                  </p>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default ComingSoonPodcast